import httpRequest from "@/apis/axios-api";

export interface CheckinResult {
  business_date: string;
  is_checked_in: boolean;
  award?: Award;
  seconds_until_next: number;
  /** 标识本次是否为真正签到成功（而非缓存命中或已签到状态） */
  just_checked_in?: boolean;
}

export interface Award {
  experience: number;
  coin: number;
}

const CHECKIN_CACHE_KEY_PREFIX = "didaoj_checkin_cache_";

interface CheckinCache {
  user_id: number;
  business_date: string;
  is_checked_in: boolean;
  expire_time: number;
}

function getCacheKey(userId: number): string {
  return `${CHECKIN_CACHE_KEY_PREFIX}${userId}`;
}

function getCache(userId: number): CheckinCache | null {
  try {
    const cacheStr = localStorage.getItem(getCacheKey(userId));
    if (!cacheStr) return null;
    const cache = JSON.parse(cacheStr) as CheckinCache;
    // 校验缓存中的 userId 是否匹配
    if (cache.user_id !== userId) {
      localStorage.removeItem(getCacheKey(userId));
      return null;
    }
    if (Date.now() > cache.expire_time) {
      localStorage.removeItem(getCacheKey(userId));
      return null;
    }
    return cache;
  } catch {
    return null;
  }
}

function setCache(userId: number, businessDate: string, isCheckedIn: boolean, secondsUntilNext: number) {
  const cache: CheckinCache = {
    user_id: userId,
    business_date: businessDate,
    is_checked_in: isCheckedIn,
    expire_time: Date.now() + secondsUntilNext * 1000,
  };
  localStorage.setItem(getCacheKey(userId), JSON.stringify(cache));
}

function clearCache(userId: number) {
  localStorage.removeItem(getCacheKey(userId));
}

export function GetCheckinStatus() {
  return httpRequest({
    url: "/user/checkin/status",
    method: "get",
  });
}

export function PostCheckin(userId: number) {
  clearCache(userId);
  return httpRequest({
    url: "/user/checkin",
    method: "post",
  });
}

/** 后端返回的"今日已签到"错误码 */
const CHECKIN_ALREADY_DONE_CODE = 100044;

export async function TryAutoCheckin(userId: number): Promise<CheckinResult | null> {
  const cache = getCache(userId);
  if (cache && cache.is_checked_in) {
    return {
      business_date: cache.business_date,
      is_checked_in: true,
      seconds_until_next: Math.max(0, Math.floor((cache.expire_time - Date.now()) / 1000)),
      just_checked_in: false,
    };
  }

  try {
    const res = await GetCheckinStatus();
    if (res.code === 0) {
      const result = res.data as CheckinResult;
      if (result.is_checked_in) {
        // 用户今天已签到（缓存过期后重新查询），不算新签到
        setCache(userId, result.business_date, true, result.seconds_until_next);
        return { ...result, just_checked_in: false, award: undefined };
      }
      const checkinRes = await PostCheckin(userId);
      if (checkinRes.code === 0) {
        // 真正签到成功
        const checkinResult = checkinRes.data as CheckinResult;
        setCache(userId, checkinResult.business_date, true, checkinResult.seconds_until_next);
        return { ...checkinResult, just_checked_in: true };
      }
      if (checkinRes.code === CHECKIN_ALREADY_DONE_CODE) {
        // 后端确认已签到（并发/缓存竞态等情况），不算新签到
        setCache(userId, result.business_date, true, result.seconds_until_next);
        return { ...result, is_checked_in: true, just_checked_in: false, award: undefined };
      }
      return { ...result, just_checked_in: false };
    }
    return null;
  } catch {
    return null;
  }
}

export function IsCacheValid(userId: number): boolean {
  const cache = getCache(userId);
  return cache !== null && cache.is_checked_in;
}

export function GetCachedCheckinStatus(userId: number): CheckinCache | null {
  return getCache(userId);
}

export function GetCheckinStaticsRecently() {
  return httpRequest({
    url: "/checkin/statics/recently",
    method: "get",
  });
}
